/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { CartModel, Item } from '@/cart/data/models';
import { state } from '@/cart/lib/state';
import {
  GiftWrappingConfigProps,
  GiftOptionsViewProps,
  ProductGiftOptionsConfig,
} from '@/cart/types';

// This helper is specifically designed for small functions needed in useGiftOptions for formSections configuration manipulation.
export const DEFAULT_FORM_STATE = {
  recipientName: '',
  senderName: '',
  message: '',
};

export const DEFAULT_CHECKBOXES_STATE = {
  giftReceiptIncluded: false,
  printedCardIncluded: false,
  isGiftWrappingSelected: false,
};

export const shouldShowGiftMessage = (
  item: CartModel['items'][0] | ProductGiftOptionsConfig,
  isProductView: boolean
) => {
  if (!isProductView) return !!state.config?.allowGiftMessageOnOrder;

  const allowGiftMessageOnOrderItems =
    state.config?.allowGiftMessageOnOrderItems ?? false;

  return typeof item?.giftMessageAvailable === 'boolean'
    ? item?.giftMessageAvailable
    : allowGiftMessageOnOrderItems;
};

export const getSelectedGiftWrapping = (
  giftWrappingOptions: GiftWrappingConfigProps[] | []
): GiftWrappingConfigProps | undefined => {
  const fallbackGiftWrappingData = {
    uid: '',
    design: '',
    selected: false,
    image: {
      url: '',
      design: '',
    },
    price: {
      currency: 'USD',
      value: 0,
    },
  };

  if (!giftWrappingOptions || giftWrappingOptions.length === 0) {
    return fallbackGiftWrappingData;
  }

  const isNotSelected = giftWrappingOptions?.every((wrap) => !wrap.selected);

  if (isNotSelected) {
    return giftWrappingOptions[0];
  }

  return giftWrappingOptions.find((wrap) => wrap.selected);
};

export const areGiftOptionsDisabled = (
  view: GiftOptionsViewProps,
  item: Item | ProductGiftOptionsConfig
) => {
  const {
    allowGiftWrappingOnOrder,
    allowGiftWrappingOnOrderItems,
    allowGiftMessageOnOrder,
    allowGiftMessageOnOrderItems,
    allowGiftReceipt,
    allowPrintedCard,
  } = state.config || {};

  const isGiftOptionsOnProductDisabled =
    !allowGiftMessageOnOrderItems &&
    !allowGiftWrappingOnOrderItems &&
    !shouldShowGiftMessage(item, true);

  const isGiftOptionsOnOrderDisabled =
    !allowGiftWrappingOnOrder &&
    !allowGiftMessageOnOrder &&
    !allowGiftReceipt &&
    !allowPrintedCard;

  if (view === 'product' && isGiftOptionsOnProductDisabled) {
    return true;
  }

  if (view === 'order' && isGiftOptionsOnOrderDisabled) {
    return true;
  }

  return false;
};
